import type { EquityFormData } from './equity-form-defaults'
import { EQUITY_PROTOCOL_FEE_BIPS } from './equity-form-mapper'

export type ValidationErrors = Record<string, string>

const ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/

const isPositiveNumber = (s: string): boolean => {
  if (s === '') return false
  const n = Number(s)
  return Number.isFinite(n) && n >= 0
}

const isStrictlyPositiveNumber = (s: string): boolean => {
  if (s === '') return false
  const n = Number(s)
  return Number.isFinite(n) && n > 0
}

const isUrl = (s: string): boolean => {
  try {
    const u = new URL(s)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

const isDatetimeLocal = (s: string): boolean => {
  if (!s) return false
  const d = new Date(s)
  return !Number.isNaN(d.getTime())
}

const requiredString = (
  errors: ValidationErrors,
  key: string,
  value: string,
  label: string,
) => {
  if (!value || !value.trim()) errors[key] = `${label} is required`
}

const positiveNumeric = (
  errors: ValidationErrors,
  key: string,
  value: string,
  label: string,
) => {
  if (!isPositiveNumber(value)) {
    errors[key] = `${label} must be a non-negative number`
  }
}

const strictlyPositiveNumeric = (
  errors: ValidationErrors,
  key: string,
  value: string,
  label: string,
) => {
  if (!isStrictlyPositiveNumber(value)) {
    errors[key] = `${label} must be greater than zero`
  }
}

const validAddress = (
  errors: ValidationErrors,
  key: string,
  value: string,
  label: string,
) => {
  if (!ADDRESS_RE.test(value)) errors[key] = `${label} is not a valid address`
}

const validUrl = (
  errors: ValidationErrors,
  key: string,
  value: string,
  label: string,
) => {
  if (!isUrl(value)) errors[key] = `${label} must be a valid URL`
}

export const validateEquityForm = (form: EquityFormData): ValidationErrors => {
  const errors: ValidationErrors = {}

  // Overview
  requiredString(errors, 'projectName', form.projectName, 'Project name')
  requiredString(
    errors,
    'projectDescription',
    form.projectDescription,
    'Project description',
  )
  requiredString(errors, 'logoUrl', form.logoUrl, 'Logo')
  if (form.categoryTags.length === 0) {
    errors.categoryTags = 'Pick at least one category'
  }
  validUrl(errors, 'websiteUrl', form.websiteUrl, 'Website URL')
  if (form.xUrl) validUrl(errors, 'xUrl', form.xUrl, 'X URL')
  if (form.discordUrl)
    validUrl(errors, 'discordUrl', form.discordUrl, 'Discord URL')
  requiredString(errors, 'pitchDeckUrl', form.pitchDeckUrl, 'Pitch deck')
  if (form.researchReportUrl)
    validUrl(
      errors,
      'researchReportUrl',
      form.researchReportUrl,
      'Research report URL',
    )
  requiredString(errors, 'dealType', form.dealType, 'Deal type')
  requiredString(errors, 'jurisdiction', form.jurisdiction, 'Jurisdiction')
  positiveNumeric(errors, 'targetRaise', form.targetRaise, 'Target raise')
  positiveNumeric(errors, 'fdv', form.fdv, 'FDV')

  // Vehicle
  requiredString(
    errors,
    'vehicleCompanyName',
    form.vehicleCompanyName,
    'Company name',
  )
  requiredString(
    errors,
    'vehicleDirectorName',
    form.vehicleDirectorName,
    'Director name',
  )
  requiredString(
    errors,
    'vehicleSignatoryName',
    form.vehicleSignatoryName,
    'Signatory name',
  )
  requiredString(
    errors,
    'vehicleSignatoryTitle',
    form.vehicleSignatoryTitle,
    'Signatory title',
  )
  requiredString(
    errors,
    'vehicleRegisteredAddress',
    form.vehicleRegisteredAddress,
    'Registered address',
  )
  requiredString(
    errors,
    'vehicleCompanyNo',
    form.vehicleCompanyNo,
    'Company number',
  )

  // Investment terms
  positiveNumeric(
    errors,
    'minRaiseThreshold',
    form.minRaiseThreshold,
    'Min raise threshold',
  )
  positiveNumeric(
    errors,
    'minInvestmentPerUser',
    form.minInvestmentPerUser,
    'Min investment per user',
  )
  if (form.useMaxDeposit) {
    positiveNumeric(errors, 'maxDeposit', form.maxDeposit, 'Max deposit')
  }
  positiveNumeric(
    errors,
    'depositChainId',
    form.depositChainId,
    'Deposit chain',
  )
  validAddress(
    errors,
    'paymentToken.address',
    form.paymentToken.address,
    'Payment token address',
  )
  requiredString(
    errors,
    'paymentToken.symbol',
    form.paymentToken.symbol,
    'Payment token symbol',
  )
  requiredString(
    errors,
    'paymentToken.name',
    form.paymentToken.name,
    'Payment token name',
  )
  positiveNumeric(
    errors,
    'paymentToken.decimals',
    form.paymentToken.decimals,
    'Payment token decimals',
  )
  positiveNumeric(
    errors,
    'distributionChainId',
    form.distributionChainId,
    'Distribution chain',
  )
  validAddress(
    errors,
    'projectToken.address',
    form.projectToken.address,
    'Project token address',
  )
  requiredString(
    errors,
    'projectToken.symbol',
    form.projectToken.symbol,
    'Project token symbol',
  )
  requiredString(
    errors,
    'projectToken.name',
    form.projectToken.name,
    'Project token name',
  )
  positiveNumeric(
    errors,
    'projectToken.decimals',
    form.projectToken.decimals,
    'Project token decimals',
  )
  strictlyPositiveNumeric(errors, 'maxCap', form.maxCap, 'Max cap')
  strictlyPositiveNumeric(errors, 'tokenPrice', form.tokenPrice, 'Token price')
  positiveNumeric(errors, 'cliffDays', form.cliffDays, 'Cliff days')
  positiveNumeric(errors, 'vestingDays', form.vestingDays, 'Vesting days')
  if (!isPositiveNumber(form.tgeUnlockPct) || Number(form.tgeUnlockPct) > 100) {
    errors.tgeUnlockPct = 'TGE unlock must be between 0 and 100'
  }

  // Timeline
  const timelineFields: Array<keyof EquityFormData> = [
    'depositStarts',
    'depositEnds',
    'shortlistNotified',
    'signAgreement',
    'firstUnlock',
    'nftTransferable',
  ]
  const timelineValues = timelineFields.map(
    (k) => form[k] as string | undefined,
  )
  if (timelineValues.some((v) => !v || !isDatetimeLocal(v as string))) {
    errors.timelines = 'All six timelines are required'
  } else {
    const ts = timelineValues.map((v) => new Date(v as string).getTime())
    const inOrder =
      ts[0] < ts[1] &&
      ts[1] <= ts[2] &&
      ts[2] <= ts[3] &&
      ts[3] <= ts[4] &&
      ts[4] <= ts[5]
    if (!inOrder) errors.timelines = 'Timelines must be in chronological order'
  }

  // Base PDFs
  if (form.equityBasePdfs.length === 0) {
    errors.equityBasePdfs = 'Upload at least one base document'
  } else {
    form.equityBasePdfs.forEach((pdf, i) => {
      if (!pdf.name || !pdf.name.trim() || !pdf.key) {
        errors[`equityBasePdfs.${i}`] =
          'Each base document needs a name and a file'
      }
    })
  }

  // Funds
  validAddress(
    errors,
    'projectReceivingWallet',
    form.projectReceivingWallet,
    'Project receiving wallet',
  )
  strictlyPositiveNumeric(
    errors,
    'curatorCommitmentAmount',
    form.curatorCommitmentAmount,
    'Curator commitment amount',
  )
  positiveNumeric(errors, 'curatorFeeBips', form.curatorFeeBips, 'Curator fee')
  validAddress(errors, 'curatorWallet', form.curatorWallet, 'Curator wallet')
  positiveNumeric(
    errors,
    'projectRoyaltyBips',
    form.projectRoyaltyBips,
    'Project royalty',
  )
  positiveNumeric(
    errors,
    'curatorRoyaltyBips',
    form.curatorRoyaltyBips,
    'Curator royalty',
  )

  const royaltyTotal =
    Number(form.projectRoyaltyBips || 0) + Number(form.curatorRoyaltyBips || 0)
  if (royaltyTotal > 10000) {
    errors.royaltyBips = 'Project + curator royalty must not exceed 10000 bips'
  }
  const feeTotal = Number(form.curatorFeeBips || 0) + EQUITY_PROTOCOL_FEE_BIPS
  if (feeTotal > 10000) {
    errors.curatorFeeBips =
      'Curator fee + protocol fee must not exceed 10000 bips'
  }

  // Visibility
  if (form.investorEligibility.length === 0) {
    errors.investorEligibility = 'Pick at least one eligibility option'
  }

  // NFT
  requiredString(errors, 'nftName', form.nftName, 'NFT name')
  requiredString(errors, 'nftSymbol', form.nftSymbol, 'NFT symbol')

  return errors
}
